import React, { useEffect, useRef, useState } from 'react';

// Recorte e compressão da foto ANTES de enviar — o passo que o WhatsApp faz e o
// Fio não fazia.
//
// O que motivou: uma foto de tablet sai com 4 MB. O Fio mandava esses 4 MB
// crus, que viram ~5,6 MB em base64 no caminho até a Evolution, numa internet
// de loja. Em 28/08/2026 um envio desses morreu no meio e o atendente só viu
// "Não deu pra enviar". Reduzindo pra ~200 KB o envio passa a ser instantâneo e
// deixa de depender de a conexão aguentar minutos de upload.
//
// Recortar e comprimir moram JUNTOS de propósito: são a mesma pergunta ("o que
// desta foto interessa mandar?"), e separar viraria duas telas pra uma decisão
// só. Quem não quer recortar aperta Usar e leva a foto inteira — já comprimida,
// que é o que resolve o problema.

// Lado maior do que sai daqui. 1600 px é mais do que qualquer tela de celular
// mostra, e ainda dá pra ler número de nota fiscal e código de produto com
// zoom — que é pra isso que fotografam planilha aqui.
const LADO_MAX = 1600;
// 0,82 é onde o JPEG para de crescer sem melhorar pra olho humano. Abaixo disso
// texto fotografado começa a borrar, e texto fotografado é metade do uso.
const QUALIDADE = 0.82;
// Abaixo de 5% da imagem o recorte vira acidente, não intenção.
const MIN_CAIXA = 0.05;

function formatarTamanho(bytes) {
  return bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    : `${Math.round(bytes / 1024)} KB`;
}

// GIF fica de fora: passar por canvas mataria a animação, e ninguém recorta um
// GIF. Vai como veio.
export function daPraEditar(arquivo) {
  const t = String(arquivo?.type || '');
  return t.startsWith('image/') && !t.includes('gif');
}

function limitar(v, min, max) { return Math.min(max, Math.max(min, v)); }

export default function EditorImagem({ arquivo, onCancelar, onPronto }) {
  const [url, setUrl] = useState(null);
  const [caixa, setCaixa] = useState({ x: 0, y: 0, w: 1, h: 1 });
  const [processando, setProcessando] = useState(false);
  const [dimensoes, setDimensoes] = useState(null);
  const areaRef = useRef(null);
  // Onde o dedo pegou: guarda a caixa do início do gesto e o ponto de partida,
  // pra o arrasto ser relativo. Sem isso a caixa pula pro dedo a cada toque.
  const gesto = useRef(null);

  useEffect(() => {
    if (!arquivo) return undefined;
    const u = URL.createObjectURL(arquivo);
    setUrl(u);
    setCaixa({ x: 0, y: 0, w: 1, h: 1 });
    return () => URL.revokeObjectURL(u);
  }, [arquivo]);

  // Girar gera uma IMAGEM NOVA em vez de só rodar o CSS. Assim o que está na
  // tela e as coordenadas do recorte nunca discordam — girar por transform
  // obrigaria a converter ângulo dentro do recorte, que é onde esse tipo de
  // tela costuma errar.
  const girar = async () => {
    if (!url) return;
    setProcessando(true);
    try {
      const img = await carregarImagem(url);
      const cv = document.createElement('canvas');
      cv.width = img.naturalHeight;
      cv.height = img.naturalWidth;
      const ctx = cv.getContext('2d');
      ctx.translate(cv.width / 2, cv.height / 2);
      ctx.rotate(Math.PI / 2);
      ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);
      const blob = await new Promise((r) => cv.toBlob(r, 'image/jpeg', 0.95));
      URL.revokeObjectURL(url);
      setUrl(URL.createObjectURL(blob));
      setCaixa({ x: 0, y: 0, w: 1, h: 1 });
      setDimensoes({ w: cv.width, h: cv.height });
    } finally { setProcessando(false); }
  };

  const pontoNaArea = (e) => {
    const r = areaRef.current.getBoundingClientRect();
    return { x: (e.clientX - r.left) / r.width, y: (e.clientY - r.top) / r.height };
  };

  const comecarGesto = (e, canto) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    gesto.current = { canto, inicio: pontoNaArea(e), caixa };
  };

  const mover = (e) => {
    if (!gesto.current) return;
    e.preventDefault();
    const p = pontoNaArea(e);
    const { canto, inicio, caixa: c0 } = gesto.current;
    const dx = p.x - inicio.x;
    const dy = p.y - inicio.y;

    if (canto === 'mover') {
      setCaixa({
        ...c0,
        x: limitar(c0.x + dx, 0, 1 - c0.w),
        y: limitar(c0.y + dy, 0, 1 - c0.h),
      });
      return;
    }
    // Cada canto move as duas bordas que encosta nele, e só elas.
    const esq = canto.includes('o');
    const cima = canto.includes('n');
    let { x, y, w, h } = c0;
    if (esq) {
      const novoX = limitar(c0.x + dx, 0, c0.x + c0.w - MIN_CAIXA);
      w = c0.w + (c0.x - novoX);
      x = novoX;
    } else {
      w = limitar(c0.w + dx, MIN_CAIXA, 1 - c0.x);
    }
    if (cima) {
      const novoY = limitar(c0.y + dy, 0, c0.y + c0.h - MIN_CAIXA);
      h = c0.h + (c0.y - novoY);
      y = novoY;
    } else {
      h = limitar(c0.h + dy, MIN_CAIXA, 1 - c0.y);
    }
    setCaixa({ x, y, w, h });
  };

  const soltar = () => { gesto.current = null; };

  const confirmar = async () => {
    setProcessando(true);
    try {
      const img = await carregarImagem(url);
      const cx = Math.round(caixa.x * img.naturalWidth);
      const cy = Math.round(caixa.y * img.naturalHeight);
      const cw = Math.max(1, Math.round(caixa.w * img.naturalWidth));
      const ch = Math.max(1, Math.round(caixa.h * img.naturalHeight));
      const escala = Math.min(1, LADO_MAX / Math.max(cw, ch));

      const cv = document.createElement('canvas');
      cv.width = Math.max(1, Math.round(cw * escala));
      cv.height = Math.max(1, Math.round(ch * escala));
      const ctx = cv.getContext('2d');
      // Fundo branco antes de desenhar: PNG com transparência viraria preto no
      // JPEG, e print de tela com fundo transparente é caso comum aqui.
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, cv.width, cv.height);
      ctx.drawImage(img, cx, cy, cw, ch, 0, 0, cv.width, cv.height);

      const blob = await new Promise((r) => cv.toBlob(r, 'image/jpeg', QUALIDADE));
      if (!blob) throw new Error('canvas nao gerou imagem');
      const nome = String(arquivo.name || 'foto').replace(/\.[^.]+$/, '') || 'foto';
      onPronto(new File([blob], `${nome}.jpg`, { type: 'image/jpeg' }), {
        antes: arquivo.size, depois: blob.size,
      });
    } catch (e) {
      // Não dá pra processar (imagem gigante, memória curta do tablet)? Manda a
      // original em vez de travar o atendente — pior que enviar pesado é não
      // conseguir enviar.
      console.error('[editor imagem] caiu pro original:', e.message);
      onPronto(arquivo, null);
    } finally { setProcessando(false); }
  };

  if (!arquivo || !url) return null;

  const recortou = caixa.w < 0.999 || caixa.h < 0.999;
  const pct = (v) => `${v * 100}%`;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(8,23,43,0.9)',
      display: 'flex', flexDirection: 'column',
    }}>
      <header style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '12px 16px', color: '#fff', flexShrink: 0,
      }}>
        <strong style={{ fontSize: '0.95rem' }}>Ajustar foto</strong>
        <span style={{ fontSize: '0.78rem', opacity: 0.75 }}>
          {formatarTamanho(arquivo.size)}
          {dimensoes ? ` · ${dimensoes.w}×${dimensoes.h}` : ''}
        </span>
      </header>

      <div style={{
        flex: 1, minHeight: 0, display: 'flex', alignItems: 'center',
        justifyContent: 'center', padding: '0 12px', overflow: 'hidden',
      }}>
        <div
          ref={areaRef}
          style={{ position: 'relative', display: 'inline-block', maxWidth: '100%', maxHeight: '100%', touchAction: 'none' }}
          onPointerMove={mover}
          onPointerUp={soltar}
          onPointerCancel={soltar}
        >
          <img
            src={url}
            alt="Foto a enviar"
            draggable={false}
            onLoad={(e) => setDimensoes({ w: e.target.naturalWidth, h: e.target.naturalHeight })}
            style={{ display: 'block', maxWidth: '100%', maxHeight: '72vh', userSelect: 'none' }}
          />
          {/* O escurecido de fora é uma sombra gigante na própria caixa: um
              elemento em vez de quatro, e acompanha o recorte sozinho. */}
          <div
            onPointerDown={(e) => comecarGesto(e, 'mover')}
            style={{
              position: 'absolute',
              left: pct(caixa.x), top: pct(caixa.y),
              width: pct(caixa.w), height: pct(caixa.h),
              border: '1px solid rgba(255,255,255,0.9)',
              boxShadow: '0 0 0 9999px rgba(0,0,0,0.55)',
              cursor: 'move', touchAction: 'none',
            }}
          >
            {['no', 'ne', 'so', 'se'].map((canto) => (
              <span
                key={canto}
                onPointerDown={(e) => comecarGesto(e, canto)}
                style={{
                  position: 'absolute',
                  [canto.includes('n') ? 'top' : 'bottom']: -11,
                  [canto.includes('o') ? 'left' : 'right']: -11,
                  // 22px: alvo que o dedo acerta. Menor que isso obriga a mirar,
                  // e quem está no balcão com o cliente na frente não mira.
                  width: 22, height: 22, borderRadius: '50%',
                  background: '#fff', border: '2px solid #3D8B37',
                  touchAction: 'none', cursor: 'pointer',
                }}
              />
            ))}
          </div>
        </div>
      </div>

      <footer style={{
        display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'space-between',
        padding: '12px 16px', flexShrink: 0,
      }}>
        <button type="button" onClick={onCancelar} disabled={processando}
          className="btn btn-sm btn-outline-light">
          Cancelar
        </button>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {recortou && (
            <button type="button" onClick={() => setCaixa({ x: 0, y: 0, w: 1, h: 1 })}
              disabled={processando} className="btn btn-sm btn-outline-light">
              Foto inteira
            </button>
          )}
          <button type="button" onClick={girar} disabled={processando}
            className="btn btn-sm btn-outline-light" title="Girar 90°">
            <i className="bi bi-arrow-clockwise" />
          </button>
          <button type="button" onClick={confirmar} disabled={processando}
            className="btn btn-sm"
            style={{ background: '#3D8B37', color: '#fff', border: 'none', minWidth: 96 }}>
            {processando ? 'Ajustando…' : 'Usar'}
          </button>
        </div>
      </footer>
    </div>
  );
}

function carregarImagem(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('não deu pra ler a imagem'));
    img.src = src;
  });
}
